import React, { useState, useEffect, useCallback } from 'react';
import { employeeAPI, salesAPI } from '../services/api.ts';
import { Layout } from '../components/Layout.tsx';
import { Toast } from '../components/Toast.tsx';
import FilterModal from '../components/FilterModal.tsx';
import dayjs from 'dayjs';
import {
  InformationCircleIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import EmployeeInfoModal from '../components/Modal/EmployeeInfo.tsx';

export const Sales = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [filterByGroup, setFilterByGroup] = useState('');
  const [filterDataByGroup, setFilterDataByGroup] = useState<any[]>([]);
  const [checkboxStates, setCheckboxStates] = useState('');
  const [dateFilter, setDateFilter] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);

  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const checkboxProps = [
    { id: 'employee', label: 'Group of Employee' },
    { id: 'customer', label: 'Group of Customer' },
    { id: 'Territory', label: 'Group of Territory' },
    { id: 'company', label: 'Group of Company' },
  ];

  const handleApply = async (
    startDate: string,
    endDate: string,
    checkboxState: any,
  ) => {
    if (checkboxState) {
      setFilterByGroup(checkboxState);
      setIsModalOpen(false);
    }
    try {
      setLoading(true);

      if (startDate && endDate) {
        const response = await salesAPI.getSalesFilter(
          page,
          50,
          startDate,
          endDate,
        );
        setProjects(response.data as never as any[]);
        setDateFilter(true);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalSales || 0);
        setIsModalOpen(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await salesAPI.getAllSales(page);
      if (response?.data) {
        setProjects(response.data as never as any[]);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalSales || 0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchProjects();
  }, [page, fetchProjects]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProjects(projects);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filteredData = projects.filter((project) => {
      const orderSequence = project?.order_sequence?.toLowerCase() || '';
      const employeeName = project?.employee?.name?.toLowerCase() || '';
      const partnerName = project?.partner?.name?.toLowerCase() || '';
      const territoryName = project?.territory?.name?.toLowerCase() || '';
      const companyName = project?.company?.name?.toLowerCase() || '';

      return (
        orderSequence.includes(lowerSearch) ||
        employeeName.includes(lowerSearch) ||
        partnerName.includes(lowerSearch) ||
        territoryName.includes(lowerSearch) ||
        companyName.includes(lowerSearch)
      );
    });

    setFilteredProjects(filteredData);
  }, [searchTerm, projects]);

  useEffect(() => {
    if (!filterByGroup) return;

    let groupedProjects: any[] = [];

    // Group by Employee, Territory, Company, or Customer
    if (filterByGroup === 'employee') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const employeeName = project.employee?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.employeeName === employeeName,
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ employeeName, projects: [project] });
        }

        return acc;
      }, []);
    }

    if (filterByGroup === 'Territory') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const territoryName = project.territory?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.territoryName === territoryName,
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ territoryName, projects: [project] });
        }

        return acc;
      }, []);
    }

    if (filterByGroup === 'company') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const companyName = project.company?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.companyName === companyName,
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ companyName, projects: [project] });
        }

        return acc;
      }, []);
    }

    if (filterByGroup === 'customer') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const customerName = project.partner?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.customerName === customerName,
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ customerName, projects: [project] });
        }

        return acc;
      }, []);
    }

    setFilterDataByGroup(groupedProjects);
  }, [filterByGroup, projects]);

  const resetFilter = () => {
    setFilterByGroup('');
    setFilterDataByGroup([]);
    setSearchTerm('');
    setCheckboxStates('');
    setDateFilter(false);
    fetchProjects();
  };

  useEffect(() => {
    if (selectedEmployee) {
      setShowEmployeeDetails(true);
      employeeAPI.details(selectedEmployee).then((res) => {
        if (res.data) {
          setEmployeeDetails(res.data);
        }
      });
      return () => {
        setShowEmployeeDetails(false);
        setEmployeeDetails(null);
      };
    }
  }, [selectedEmployee]);

  return (
    <Layout>
      <div className="py-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Sales
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  Track and manage sales orders
                </p>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="search"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Search by order, employee, customer, territory, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setFilterDataByGroup([])}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg"
                >
                  <FunnelIcon className="h-5 w-5" />
                  Filter
                </button>
                {(filterDataByGroup.length > 0 || dateFilter) && (
                  <button
                    onClick={resetFilter}
                    className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-all shadow-md hover:shadow-lg"
                  >
                    <XMarkIcon className="h-5 w-5" />
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-3" />
                <p className="text-green-800 dark:text-green-200 font-medium text-sm flex-1">
                  {successMessage}
                </p>
                <button
                  type="button"
                  onClick={() => setSuccessMessage('')}
                  className="p-1 hover:bg-green-100 dark:hover:bg-green-800 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-3" />
                <p className="text-red-800 dark:text-red-200 font-medium text-sm flex-1">
                  {error}
                </p>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 dark:border-green-900"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-green-600 dark:border-green-400 absolute top-0 left-0"></div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Order Sequence
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Employee
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Customer
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Territory
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Status
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Company
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Total
                    </th>
                    <th className="border dark:border-gray-600 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Created At
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.length > 0 &&
                  filterDataByGroup.length === 0 ? (
                    filteredProjects.map((project) => (
                      <tr
                        key={project._id}
                        className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {project.order_sequence}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {project.employee?.name || 'Odoo'}{' '}
                          {project.employee?.name && (
                            <InformationCircleIcon
                              className="h-4 w-4 text-gray-500 dark:text-gray-400 inline-block cursor-pointer hover:text-primary-600 dark:hover:text-primary-400"
                              onClick={() =>
                                setSelectedEmployee(project.employee?.id)
                              }
                            />
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {project.partner?.name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {project.territory?.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          <span
                            className={`px-2 py-1 rounded ${
                              project.odooStatus === 'Sale Order'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : project.odooStatus === 'Quotation'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }`}
                          >
                            {project.odooStatus}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {project.company?.name || 'N/A'}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {project.total.toFixed(2)} PKR
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                          {dayjs(project.createdAt).format(
                            'DD-MM-YYYY HH:mm:ss',
                          )}
                        </td>
                      </tr>
                    ))
                  ) : filterDataByGroup.length > 0 ? (
                    <>
                      {filterDataByGroup.map((group) => (
                        <React.Fragment
                          key={
                            group.employeeName ||
                            group.territoryName ||
                            group.companyName ||
                            group.customerName
                          }
                        >
                          <tr>
                            <td
                              colSpan={7}
                              className="text-gray-900 dark:text-white font-semibold px-3 py-3 bg-gray-50 dark:bg-gray-700/50"
                            >
                              {group.employeeName ||
                                group.territoryName ||
                                group.companyName ||
                                group.customerName}
                            </td>
                          </tr>
                          {group.projects.map((project: any) => (
                            <tr key={project._id} className="border-b">
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {project.order_sequence}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {project.employee?.name}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {project.partner?.name}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {project.territory?.name}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                                <span
                                  className={`px-2 py-1 rounded ${
                                    project.odooStatus === 'Sale Order'
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                      : project.odooStatus === 'Quotation'
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                  }`}
                                >
                                  {project.odooStatus}
                                </span>
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {project.company?.name || 'N/A'}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {project.total.toFixed(2)} PKR
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-300">
                                {dayjs(project.createdAt).format(
                                  'DD-MM-YYYY HH:mm:ss',
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center p-4 text-gray-500 dark:text-gray-400"
                      >
                        No sales found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Pagination - Only show when there are projects */}
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing{' '}
                      <span className="font-medium">
                        {Math.min((page - 1) * 50 + 1, totalItems)}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(page * 50, totalItems)}
                      </span>{' '}
                      of <span className="font-medium">{totalItems}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={page === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <FilterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onApply={handleApply}
        setCheckboxStates={setCheckboxStates}
        checkboxStates={checkboxStates}
        checkboxProps={checkboxProps}
      />

      {showEmployeeDetails && employeeDetails && (
        <EmployeeInfoModal
          employee={employeeDetails}
          onClose={() => setShowEmployeeDetails(false)}
        />
      )}
    </Layout>
  );
};
